import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths,
  isSameMonth, isSameDay, isToday, getHours, getMinutes, setHours, setMinutes, parseISO
} from 'date-fns';
import { cn } from '../../lib/utils';
import { canEditEvent, rbac } from '../../lib/rbac';
import useCalendarStore from '../../store/useCalendarStore';

const HOUR_PX = 60;
const TOTAL_HOURS = 24;
const TOTAL_PX = HOUR_PX * TOTAL_HOURS;
const SNAP_MINUTES = 15;
const DAYS_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const COLOR_MAP = {
  blue: '#4285f4', violet: '#7c3aed', rose: '#e11d48',
  emerald: '#059669', orange: '#ea580c', amber: '#d97706',
  indigo: '#4f46e5', teal: '#0d9488', pink: '#db2777',
  slate: '#64748b', cyan: '#06b6d4', lime: '#84cc16',
  yellow: '#facc15', red: '#ef4444', purple: '#a855f7',
  fuchsia: '#d946ef', green: '#22c55e',
};
function getColor(c) { return COLOR_MAP[c] || COLOR_MAP.blue; }

function minToY(m) { return (m / 60) * HOUR_PX; }
function yToMin(y) { return Math.round((y / HOUR_PX) * 60); }
function snapMin(m) { return Math.round(m / SNAP_MINUTES) * SNAP_MINUTES; }
function formatHour(h) { if (h === 0) return ''; return `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`; }
function formatMinutes(m) { const h = Math.floor(m / 60); const mm = m % 60; return `${h % 12 || 12}:${String(mm).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`; }

function eventToMinutes(ev) {
  const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
  const e = typeof ev.end_time === 'string' ? parseISO(ev.end_time) : ev.end_time;
  return { startMin: getHours(s) * 60 + getMinutes(s), endMin: getHours(e) * 60 + getMinutes(e) };
}

function getEventLabel(ev, faculty = [], batches = [], assignments = []) {
  let fId = ev.faculty_id || ev.entity_id;
  let bId = ev.batch_id || ev.entity_id;

  if (!fId && !bId && ev.assignment_id && assignments.length > 0) {
    const assign = assignments.find(a => String(a.id) === String(ev.assignment_id));
    if (assign) {
      fId = assign.faculty_id;
      bId = assign.batch_id;
    }
  }

  const fName = faculty.find(f => String(f.id) === String(fId))?.name;
  
  // Resolve all batch names (primary bId and any batch_* from calendar_ids)
  const batchIds = new Set();
  if (bId) batchIds.add(String(bId));
  if (ev.calendar_ids && Array.isArray(ev.calendar_ids)) {
    ev.calendar_ids.forEach(cid => {
      if (typeof cid === 'string' && cid.startsWith('batch_')) {
        batchIds.add(cid.substring(6));
      }
    });
  }
  const eventBatches = Array.from(batchIds)
    .map(bid => batches.find(b => String(b.id) === String(bid))?.name)
    .filter(Boolean);
  const bNameCombined = eventBatches.join(', ');

  if (ev.event_type !== 'availability' && ev.event_type !== 'holiday') {
    const mainTitle = ev.title || 'Untitled';
    let parts = [];
    if (fName) parts.push(fName);
    if (bNameCombined) parts.push(bNameCombined);
    
    if (parts.length > 0) {
      // If it's a generic "Lecture" or "Lecture Scheduled", just show Batch - Faculty
      if (mainTitle.toLowerCase() === 'lecture' || mainTitle.toLowerCase() === 'lecture scheduled') {
        return parts.join(' • ');
      }
      return `${mainTitle} | ${parts.join(' • ')}`;
    }
    return mainTitle;
  }

  const isGeneric = !ev.title ||
    ev.title === 'Preferred' ||
    ev.title === 'Unavailable' ||
    ev.title.includes('faculty - preferred') ||
    ev.title.includes('batch - preferred');

  if (!isGeneric) return ev.title;

  const mode = ev.availability_mode === 'unavailable' || ev.event_type === 'holiday' ? 'Unavailable' : 'Preferred';
  let name = '';
  if (ev.entity_type === 'faculty') {
    name = fName || 'Faculty';
  } else if (ev.entity_type === 'batch') {
    name = bName || 'Batch';
  }

  return name ? `${name} - ${mode}` : mode;
}


// Column overlap layout (from reference HTML gcLayout)
function layoutEvents(events) {
  const sorted = [...events].sort((a, b) => {
    const am = eventToMinutes(a), bm = eventToMinutes(b);
    return am.startMin - bm.startMin || bm.endMin - am.endMin;
  });
  const columns = [];
  const result = [];
  for (const ev of sorted) {
    const { startMin, endMin } = eventToMinutes(ev);
    let col = columns.findIndex(end => end <= startMin);
    if (col === -1) col = columns.length;
    columns[col] = endMin;
    // Find total cols for this event
    const overlapping = result.filter(r => {
      const rm = eventToMinutes(r.ev);
      return rm.startMin < endMin && rm.endMin > startMin;
    });
    const totalCols = Math.max(col + 1, ...overlapping.map(o => o.col + 1));
    // Update previous overlapping
    overlapping.forEach(o => { o.totalCols = Math.max(o.totalCols, totalCols); });
    result.push({ ev, col, totalCols, startMin, endMin });
  }
  // Second pass: normalize totalCols
  for (const r of result) {
    const overlapping = result.filter(o => o !== r && o.startMin < r.endMin && o.endMin > r.startMin);
    r.totalCols = Math.max(r.totalCols, ...overlapping.map(o => o.totalCols));
  }
  return result;
}


// ─── Time Grid (Day/Week) ──────────────────────────────
export function TimeGrid({ events, allEvents, days, onSlotClick, onEventClick, onDragCreate, onEventUpdate, onTemplateDrop, onStagedDiscard, stagedTemplates = {}, highlights, currentTime, showAvailFor, painterMode, faculty, batches, conflictEventIds = new Set() }) {
  const draggedTemplate = useCalendarStore(s => s.draggedTemplate);
  const assignments = useCalendarStore(s => s.assignments);
  const settings = useCalendarStore(s => s.settings);
  // Compute grid start offset from settings (backend slots are relative to working_hours_start)
  const gridStartMinutes = (() => {
    const wh = settings?.working_hours_start || '08:00';
    const [h, m] = wh.split(':').map(Number);
    return h * 60 + (m || 0);
  })();
  const scrollRef = useRef(null);
  // Interaction gate: prevents click from firing after drag/resize
  const interactingRef = useRef(false);
  // React-driven drag hover state (replaces raw DOM ghostRef)
  const [dragHoverDayStr, setDragHoverDayStr] = useState(null);

  useEffect(() => {
    if (scrollRef.current) {
      // User requested the calendar grid be scrolled to 6am by default
      scrollRef.current.scrollTop = 6 * HOUR_PX;
    }
  }, []);

  // ── Pre-group events by day for visual rendering ──
  const { eventsByDay, allDayByDay } = useMemo(() => {
    const eMap = {};
    const adMap = {};
    for (const ev of events) {
      const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
      const key = format(s, 'yyyy-MM-dd');
      if (ev.is_all_day) {
        if (!adMap[key]) adMap[key] = [];
        adMap[key].push(ev);
      } else if (ev.event_type !== 'availability' && ev.event_type !== 'holiday') {
        if (!eMap[key]) eMap[key] = [];
        eMap[key].push(ev);
      }
    }
    return { eventsByDay: eMap, allDayByDay: adMap };
  }, [events]);

  // ── Pre-group ALL events for background unavailabilities and drag conflicts ──
  const { availByDay, conflictEventsByDay } = useMemo(() => {
    const aMap = {};
    const cMap = {};
    const sourceEvents = allEvents || events; // fallback to events if allEvents not provided
    for (const ev of sourceEvents) {
      const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
      const key = format(s, 'yyyy-MM-dd');
      if (ev.event_type === 'availability' || ev.event_type === 'holiday') {
        if (!aMap[key]) aMap[key] = [];
        aMap[key].push(ev);
      } else if (!ev.is_all_day) {
        if (!cMap[key]) cMap[key] = [];
        cMap[key].push(ev);
      }
    }
    return { availByDay: aMap, conflictEventsByDay: cMap };
  }, [allEvents, events]);

  const getEventsForDay = useCallback((day) => {
    return eventsByDay[format(day, 'yyyy-MM-dd')] || [];
  }, [eventsByDay]);

  const getConflictEventsForDay = useCallback((day) => {
    return conflictEventsByDay[format(day, 'yyyy-MM-dd')] || [];
  }, [conflictEventsByDay]);

  const getAvailForDay = useCallback((day) => {
    return availByDay[format(day, 'yyyy-MM-dd')] || [];
  }, [availByDay]);

  // Convert highlights (free slot intervals from search) to minute ranges for a given day
  // highlights come as [{start_time: "08:00", end_time: "12:00", duration_minutes: 240, date: "2026-03-07", ...}]
  const getHighlightsForDay = useCallback((day) => {
    if (!highlights?.length) return [];

    // Filter highlights that belong to the current day columns
    const dayStr = format(day, 'yyyy-MM-dd');
    const dayHighlights = highlights.filter(h => h.date === dayStr);

    return dayHighlights.map((h, idx) => {
      const [sh, sm] = h.start_time.split(':').map(Number);
      const [eh, em] = h.end_time.split(':').map(Number);
      return { startMin: sh * 60 + sm, endMin: eh * 60 + em, idx, label: `${h.start_time} - ${h.end_time} (${h.duration_minutes}m)` };
    });
  }, [highlights]);

  // ── DRAG & DROP FOR TEMPLATES (React state-driven cutout overlay) ──
  // Clean up dragHoverDayStr on global dragend (e.g. user drops outside calendar)
  useEffect(() => {
    const onDragEnd = () => setDragHoverDayStr(null);
    window.addEventListener('dragend', onDragEnd);
    return () => window.removeEventListener('dragend', onDragEnd);
  }, []);

  const handleDragOver = useCallback((e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const dayStr = format(day, 'yyyy-MM-dd');
    setDragHoverDayStr(prev => prev === dayStr ? prev : dayStr);
  }, []);

  const handleDragLeave = useCallback((e) => {
    // Only clear if we actually left the column, not just hovered over a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragHoverDayStr(null);
    }
  }, []);

  const handleDrop = useCallback((e, day) => {
    e.preventDefault();
    setDragHoverDayStr(null);
    const data = e.dataTransfer.getData('application/json');
    if (data) {
      try {
        const payload = JSON.parse(data);
        if (payload.type === 'template') {
          onTemplateDrop?.(payload, day, 0);
        }
      } catch (err) { }
    }
  }, [onTemplateDrop]);

  // ── DRAG TO CREATE (uses raw DOM to avoid React re-render thrashing) ─────
  const handleBgMouseDown = (e, day, col) => {
    if (e.button !== 0) return;
    // ── RBAC: view-only users cannot create events ──
    if (rbac.isViewOnly()) {
      console.warn('[TimeGrid] handleBgMouseDown: RBAC blocked interaction (view-only)');
      return;
    }
    console.log('[TimeGrid] handleBgMouseDown: starting drag-create', { day: format(day, 'yyyy-MM-dd'), clientY: e.clientY });
    e.preventDefault(); 
    interactingRef.current = true;

    const colRect = col.getBoundingClientRect();
    const y = e.clientY - colRect.top;
    const startMin = snapMin(yToMin(y));
    let endMin = startMin + SNAP_MINUTES;

    // Create a DOM preview element instead of setting React state
    const preview = document.createElement('div');
    const isAvailPref = painterMode && painterMode.availability_mode === 'preferred';
    const bgCls = painterMode ? (isAvailPref ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)') : 'rgba(96,165,250,0.3)';
    const borderCls = painterMode ? (isAvailPref ? '2px solid rgba(52,211,153,0.6)' : '2px solid rgba(248,113,113,0.6)') : '2px solid rgba(96,165,250,0.6)';
    const textColor = painterMode ? (isAvailPref ? '#065f46' : '#991b1b') : '#1e40af';
    
    // Higher z-index and more prominent styling
    preview.style.cssText = `position:absolute;left:2px;right:2px;border-radius:6px;z-index:100;pointer-events:none;background:${bgCls};border:${borderCls};box-shadow:0 4px 12px rgba(0,0,0,0.1);`;
    preview.style.top = minToY(startMin) + 'px';
    preview.style.height = Math.max(12, minToY(endMin - startMin)) + 'px';
    
    const label = document.createElement('span');
    label.style.cssText = `font-size:10px;font-weight:700;padding:2px 8px;color:${textColor};display:block;`;
    label.textContent = `${formatMinutes(startMin)} - ${formatMinutes(endMin)}`;
    preview.appendChild(label);
    col.appendChild(preview);

    const onMove = (me) => {
      const my = me.clientY - colRect.top;
      endMin = Math.max(startMin + SNAP_MINUTES, snapMin(yToMin(my)));
      preview.style.height = Math.max(12, minToY(endMin - startMin)) + 'px';
      label.textContent = `${formatMinutes(startMin)} - ${formatMinutes(endMin)}`;
    };
    const onUp = () => {
      console.log('[TimeGrid] handleBgMouseDown end', startMin, endMin);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      preview.remove();
      
      // Delay resetting interactingRef to catch the final click event
      setTimeout(() => { interactingRef.current = false; }, 50);

      if (endMin - startMin >= SNAP_MINUTES) {
        onDragCreate?.(day, startMin, endMin);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── DRAG TO MOVE EVENT ─────────────────────────────────
  const handleEventMouseDown = (e, ev, dayIndex) => {
    if (e.button !== 0) return;
    // Don't start move if clicking on a resize handle
    if (e.target.closest('[data-rz]')) return;
    // ── RBAC: skip move for events outside scope ──
    if (rbac.isViewOnly()) return;
    if (!canEditEvent(ev)) {
      console.warn('[TimeGrid] handleEventMouseDown: user cannot edit this specific event');
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const { startMin, endMin } = eventToMinutes(ev);
    const duration = endMin - startMin;
    const evEl = e.currentTarget;
    const offsetMin = yToMin(e.clientY - evEl.getBoundingClientRect().top);
    const startX = e.clientX, startY = e.clientY;
    const numDays = days.length;
    let moved = false, ghost = null, preview = null;
    let curDayIndex = dayIndex, curStartMin = startMin;

    const onMove = (me) => {
      me.preventDefault();
      const dx = me.clientX - startX, dy = me.clientY - startY;
      // 7px dead zone before committing to drag
      if (!moved && Math.hypot(dx, dy) > 7) {
        moved = true;
        interactingRef.current = true;
        evEl.style.opacity = '0.25';
        evEl.style.pointerEvents = 'none';
        // Create floating ghost that follows cursor
        ghost = document.createElement('div');
        ghost.style.cssText = `position:fixed;pointer-events:none;z-index:9999;border-radius:5px;padding:5px 12px;font-size:12px;color:#fff;font-family:system-ui,sans-serif;font-weight:600;box-shadow:0 6px 24px rgba(0,0,0,.35);white-space:nowrap;transform:rotate(2.5deg) scale(1.05);background:${getColor(ev.color)};`;
        ghost.textContent = ev.title || 'No title';
        document.body.appendChild(ghost);
      }
      if (!moved) return;

      // Position ghost
      ghost.style.left = (me.clientX + 18) + 'px';
      ghost.style.top = (me.clientY - 16) + 'px';

      // Find which day column the cursor is over
      let hovDayIndex = curDayIndex;
      for (let di = 0; di < numDays; di++) {
        const colEl = scrollRef.current?.querySelector(`[data-testid="day-col-${format(days[di], 'yyyy-MM-dd')}"]`);
        if (colEl) {
          const r = colEl.getBoundingClientRect();
          if (me.clientX >= r.left && me.clientX < r.right) { hovDayIndex = di; break; }
        }
      }

      // Calculate target time from cursor Y
      const targetCol = scrollRef.current?.querySelector(`[data-testid="day-col-${format(days[hovDayIndex], 'yyyy-MM-dd')}"]`);
      if (targetCol) {
        const relY = me.clientY - targetCol.getBoundingClientRect().top;
        const snapped = Math.max(0, Math.min(24 * 60 - duration, snapMin(yToMin(relY) - offsetMin)));
        curDayIndex = hovDayIndex;
        curStartMin = snapped;

        // Update / create translucent preview in target column
        if (preview) preview.remove();
        preview = document.createElement('div');
        const pEnd = curStartMin + duration;
        preview.style.cssText = `position:absolute;top:${minToY(curStartMin)}px;height:${Math.max(minToY(duration), 18)}px;left:2px;right:2px;border-radius:5px;background:${getColor(ev.color)};opacity:.4;z-index:35;pointer-events:none;border:2px dashed rgba(255,255,255,.55);`;
        const tl = document.createElement('div');
        tl.style.cssText = 'font-size:10px;color:#fff;padding:3px 5px;font-weight:600;';
        tl.textContent = `${formatMinutes(curStartMin)} – ${formatMinutes(pEnd)}`;
        preview.appendChild(tl);
        targetCol.appendChild(preview);
      }
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      evEl.style.opacity = '';
      evEl.style.pointerEvents = '';
      if (ghost) ghost.remove();
      if (preview) preview.remove();

      if (moved) {
        const newDay = days[curDayIndex];
        const newEndMin = curStartMin + duration;
        const newStart = setMinutes(setHours(new Date(newDay), Math.floor(curStartMin / 60)), curStartMin % 60);
        const newEnd = setMinutes(setHours(new Date(newDay), Math.floor(newEndMin / 60)), newEndMin % 60);
        onEventUpdate?.(ev, { start_time: newStart.toISOString(), end_time: newEnd.toISOString() });
        setTimeout(() => { interactingRef.current = false; }, 100);
      }
    };

    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onUp);
  };

  // ── RESIZE EVENT ───────────────────────────────────────
  const handleResizeMouseDown = (e, ev, edge) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    // ── RBAC: skip resize for events outside scope ──
    if (!canEditEvent(ev)) return;

    const { startMin, endMin } = eventToMinutes(ev);
    const evEl = e.target.closest('[data-evid]');
    if (!evEl) return;

    interactingRef.current = true;
    evEl.style.opacity = '0.55';
    evEl.style.transition = 'none';
    const startY = e.clientY;
    let newStartMin = startMin, newEndMin = endMin;

    const onMove = (me) => {
      me.preventDefault();
      const dMin = yToMin(me.clientY - startY);

      if (edge === 'bottom') {
        newEndMin = Math.max(startMin + SNAP_MINUTES, Math.min(24 * 60, snapMin(endMin + dMin)));
        evEl.style.height = Math.max(minToY(newEndMin - startMin), minToY(SNAP_MINUTES)) + 'px';
      } else {
        newStartMin = Math.max(0, Math.min(endMin - SNAP_MINUTES, snapMin(startMin + dMin)));
        evEl.style.top = minToY(newStartMin) + 'px';
        evEl.style.height = Math.max(minToY(endMin - newStartMin), minToY(SNAP_MINUTES)) + 'px';
      }

      // Update time label inside the event
      let timeLbl = evEl.querySelector('[data-timelbl]');
      if (timeLbl) {
        timeLbl.textContent = `${formatMinutes(edge === 'top' ? newStartMin : startMin)} – ${formatMinutes(edge === 'bottom' ? newEndMin : endMin)}`;
      }
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      evEl.style.opacity = '';
      evEl.style.transition = '';

      if (newStartMin !== startMin || newEndMin !== endMin) {
        const evStart = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
        const dayBase = new Date(evStart);
        dayBase.setHours(0, 0, 0, 0);
        const newStart = setMinutes(setHours(dayBase, Math.floor(newStartMin / 60)), newStartMin % 60);
        const newEnd = setMinutes(setHours(dayBase, Math.floor(newEndMin / 60)), newEndMin % 60);
        onEventUpdate?.(ev, { start_time: newStart.toISOString(), end_time: newEnd.toISOString() });
      }
      setTimeout(() => { interactingRef.current = false; }, 100);
    };

    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onUp);
  };

  // ── CLICK GATE ─────────────────────────────────────────
  const handleEventClick = (e, ev) => {
    e.stopPropagation();
    if (interactingRef.current) return;
    onEventClick?.(ev);
  };

  const numDays = days.length;
  const cols = numDays === 1 ? '56px 1fr' : `56px repeat(${numDays}, 1fr)`;

  return (
    <div className="flex flex-col flex-1 overflow-hidden min-h-0" data-testid="time-grid">
      {/* Day Headers */}
      <div className="grid sticky top-0 z-30 border-b border-zinc-200 dark:border-zinc-700/50 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md" style={{ gridTemplateColumns: cols }}>
        <div className="border-r border-zinc-200 dark:border-zinc-700/50" />
        {days.map((day, i) => (
          <div key={i} className={cn("text-center py-2 border-r border-zinc-200 dark:border-zinc-700/50 last:border-r-0", i > 0 && "border-l border-zinc-200 dark:border-zinc-700/50")} data-testid={`day-header-${format(day, 'yyyy-MM-dd')}`}>
            <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-zinc-400">{format(day, 'EEE')}</div>
            <div className={cn("inline-flex items-center justify-center w-11 h-11 rounded-full text-[1.625rem] font-light mt-1 cursor-pointer transition-colors", isToday(day) ? "bg-blue-600 text-white" : "text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800")}
              onClick={() => onSlotClick?.(day, 9)}>
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>

      {/* All-Day Events Row */}
      {days.some(day => (allDayByDay[format(day, 'yyyy-MM-dd')] || []).length > 0) && (
        <div className="grid border-b border-zinc-200 dark:border-zinc-700/50 bg-zinc-50/50 dark:bg-zinc-900/80" style={{ gridTemplateColumns: cols }}>
          <div className="border-r border-zinc-200 dark:border-zinc-700/50 flex items-center justify-center">
            <span className="text-[0.5625rem] text-zinc-400 uppercase tracking-wider">all-day</span>
          </div>
          {days.map((day, i) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const allDayEvs = allDayByDay[dayStr] || [];
            return (
              <div key={i} className="border-r border-zinc-200 dark:border-zinc-700/50 last:border-r-0 px-1 py-1 min-h-[1.75rem] flex flex-wrap gap-0.5" data-testid={`allday-col-${dayStr}`}>
                {allDayEvs.map(ev => (
                    <div
                      key={ev.instance_id || ev.id}
                      className="text-[0.625rem] font-medium px-2 py-0.5 rounded truncate cursor-pointer text-white"
                      style={{ background: getColor(ev.color), maxWidth: '100%' }}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(ev); }}
                      title={getEventLabel(ev, faculty, batches, assignments)}
                      data-testid={`allday-event-${ev.instance_id || ev.id}`}
                    >
                      {getEventLabel(ev, faculty, batches, assignments)}
                    </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Scroll area with time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden relative min-h-0">
        <div className="grid relative" style={{ gridTemplateColumns: cols }}>
          {/* Time gutter */}
          <div className="border-r border-zinc-200 dark:border-zinc-700/50 relative" style={{ height: TOTAL_PX }}>
            {Array.from({ length: TOTAL_HOURS }, (_, h) => (
              <div key={h} className="absolute w-full" style={{ top: h * HOUR_PX }}>
                <span className="absolute -top-[0.4375rem] right-2 text-[0.65625rem] text-zinc-400 select-none">{formatHour(h)}</span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, di) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayEvents = getEventsForDay(day);
            const laid = layoutEvents(dayEvents);
            const staged = stagedTemplates[dayStr];

            let isDimmed = false;
            if (draggedTemplate) {
              const tEvents = typeof draggedTemplate.events === 'string' ? JSON.parse(draggedTemplate.events) : draggedTemplate.events;
              const unavails = getAvailForDay(day).filter(a => a.availability_mode === 'unavailable' || a.event_type === 'holiday');
              const existingEvents = getConflictEventsForDay(day);

              isDimmed = tEvents.some(te => {
                const tStart = gridStartMinutes + te.start_slot * 15;
                const tEnd = tStart + (te.duration_slots * 15);
                const assign = assignments?.find(a => a.id === te.assignment_id);
                const tFacultyId = assign?.faculty_id || te.faculty_id;
                const tBatchId = assign?.batch_id || te.batch_id;

                // 1. Check double booking
                const hasDoubleBooking = existingEvents.some(ee => {
                  const { startMin: eStart, endMin: eEnd } = eventToMinutes(ee);
                  if (!(tStart < eEnd && tEnd > eStart)) return false; // No time overlap
                  // Same faculty or batch?
                  return ee.faculty_id === tFacultyId || ee.batch_id === tBatchId;
                });
                if (hasDoubleBooking) return true;

                // 2. Check unavailability overlap
                return unavails.some(u => {
                  const { startMin: uStart, endMin: uEnd } = eventToMinutes(u);
                  if (!(tStart < uEnd && tEnd > uStart)) return false; // No time overlap

                  // If it's a global holiday, it conflicts.
                  if (u.event_type === 'holiday') return true;

                  // If it's a specific faculty/batch unavailability, only conflict if it matches the template event
                  if (u.entity_type === 'faculty' && u.entity_id === tFacultyId) return true;
                  if (u.entity_type === 'batch' && u.entity_id === tBatchId) return true;

                  return false;
                });
              });
            }

            return (
                <div key={di} className={cn("relative border-r border-zinc-200 dark:border-zinc-700/50 last:border-r-0 transition-opacity duration-200", painterMode ? "opacity-100" : "", isDimmed ? "opacity-30 grayscale pointer-events-none" : "")} style={{ height: TOTAL_PX }} data-testid={`day-col-${dayStr}`}
                  onDragOver={(e) => handleDragOver(e, day)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, day)}
                  onMouseDown={(e) => handleBgMouseDown(e, day, e.currentTarget)}>
                  {/* Background hour slots */}
                  {Array.from({ length: TOTAL_HOURS }, (_, h) => (
                    <div key={h} className="absolute w-full border-b border-zinc-100 dark:border-zinc-800/60 cursor-crosshair" style={{ top: h * HOUR_PX, height: HOUR_PX }}
                      data-testid={`slot-${format(day, 'yyyy-MM-dd')}-${h}`}>
                      <div className="w-full border-b border-dashed border-zinc-50 dark:border-zinc-800/30" style={{ height: HOUR_PX / 2 }} />
                    </div>
                  ))}

                {/* Ghost slot highlights (free intervals from search) */}
                {getHighlightsForDay(day).length > 0 && (
                  <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 4 }}>
                    {getHighlightsForDay(day).map(gh => (
                      <div key={gh.idx}
                        className="absolute left-1 right-1 rounded-md pointer-events-auto cursor-pointer transition-all hover:brightness-95"
                        style={{
                          top: minToY(gh.startMin),
                          height: Math.max(12, minToY(gh.endMin - gh.startMin)),
                          background: 'rgba(34,197,94,0.12)',
                          border: '2px dashed rgba(34,197,94,0.5)',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDragCreate?.(day, gh.startMin, gh.endMin);
                        }}
                        title={`Free: ${gh.label} — click to schedule`}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', color: '#16a34a' }}>
                          Free: {gh.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Drag-to-create selection is now handled via raw DOM (see handleBgMouseDown) */}

                {/* Staged Template Overlay */}
                {staged && (() => {
                  const tEvents = typeof staged.payload?.events === 'string' ? JSON.parse(staged.payload.events) : (staged.payload?.events || []);
                  if (!tEvents || !tEvents.length) return null;

                  // Parse dayStr safely instead of relying on string parsing
                  const [yyyy, mm, dd] = dayStr.split('-').map(Number);
                  const pseudoEvents = tEvents.map((te, i) => {
                    const startMin = gridStartMinutes + te.start_slot * 15;
                    const durMin = te.duration_slots * 15;
                    const baseDate = new Date(yyyy, mm - 1, dd, 0, 0, 0);
                    const sDate = new Date(baseDate); sDate.setMinutes(startMin);
                    const eDate = new Date(baseDate); eDate.setMinutes(startMin + durMin);
                    const assign = assignments?.find(a => a.id === te.assignment_id);
                    return {
                      id: `tpl_staged_${i}`,
                      instance_id: `tpl_staged_${i}`,
                      title: te.subject_name || 'Event',
                      start_time: sDate,
                      end_time: eDate,
                      color: assign?.color || 'blue',
                      faculty_id: assign?.faculty_id || te.faculty_id,
                      batch_id: assign?.batch_id || te.batch_id,
                      event_type: 'lecture',
                      _isTemplatePreview: true,
                    };
                  });
                  const stagedLaid = layoutEvents(pseudoEvents);

                  return (
                    <>
                      <div className="absolute top-0 left-0 right-0 z-20 flex justify-center mt-1 pointer-events-auto" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="bg-white/95 dark:bg-zinc-800/95 text-zinc-600 dark:text-zinc-300 font-bold px-3 py-1 flex items-center gap-2 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.1)] text-[10px] border border-zinc-200 dark:border-zinc-700/50 backdrop-blur-md">
                          🏷️ Staged: {staged.payload?.name || 'Template'}
                          <button onClick={(e) => { e.stopPropagation(); onStagedDiscard?.(dayStr); }} className="hover:text-red-500 ml-1 opacity-70 hover:opacity-100 transition-opacity">✕</button>
                        </div>
                      </div>
                      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 11 }}>
                        {stagedLaid.map(({ ev: pev, col: pCol, totalCols: pTotal, startMin: pStart, endMin: pEnd }) => {
                          const pTop = minToY(pStart);
                          const pHeight = Math.max(20, minToY(pEnd - pStart));
                          const pWidth = `calc(${(1 / pTotal) * 100}% - 4px)`;
                          const pLeft = `calc(${(pCol / pTotal) * 100}% + 2px)`;
                          const bgColor = getColor(pev.color);
                          return (
                            <div key={pev.id}
                              className="absolute select-none overflow-hidden"
                              style={{
                                top: pTop || 0, height: pHeight || 0, width: pWidth, left: pLeft,
                                borderRadius: 5, background: bgColor, color: '#fff',
                                borderLeft: '4px solid rgba(0,0,0,0.2)',
                                zIndex: 11 + pCol, boxSizing: 'border-box', minHeight: 20,
                                opacity: 0.85, border: '2px dashed rgba(255,255,255,0.7)',
                              }}>
                              <div className="px-2 py-1 flex flex-col h-full min-w-0">
                                <div className="text-[0.75rem] font-semibold leading-tight truncate">{pev.title}</div>
                                
                                {pHeight > 45 && (pev.batch_id || pev.faculty_id) && (
                                  <div className="text-[0.65625rem] opacity-90 truncate leading-none font-medium mt-0.5">
                                    {[
                                      faculty.find(f => String(f.id) === String(pev.faculty_id))?.name,
                                      batches.find(b => String(b.id) === String(pev.batch_id))?.name
                                    ].filter(Boolean).join(' • ')}
                                  </div>
                                )}

                                {pHeight > 30 && <div className="text-[0.65625rem] opacity-90 mt-1">{formatMinutes(pStart)} – {formatMinutes(pEnd)}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}

                {/* Availability background layer (z-index: 5, behind events) */}
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
                  {getAvailForDay(day).filter(av => showAvailFor?.has(`${av.entity_type}_${av.entity_id}`) || av.event_type === 'holiday').map(av => {
                    const { startMin, endMin } = eventToMinutes(av);
                    const cls = av.availability_mode === 'unavailable' || av.event_type === 'holiday'
                      ? 'pattern-unavailable' : 'pattern-preferred';
                    return (
                      <div key={av.id} className={`absolute left-0.5 right-0.5 pointer-events-auto cursor-pointer transition-all hover:brightness-[0.9] dark:hover:brightness-110 ${cls}`}
                        style={{ top: minToY(startMin), height: Math.max(12, minToY(endMin - startMin)) }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (interactingRef.current) return;
                          onEventClick?.(av);
                        }}
                      >
                        <span className="avail-label truncate block px-2 text-[0.65rem] font-medium opacity-80">
                          {getEventLabel(av, faculty, batches, assignments)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Event layer */}
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
                  {laid.map(({ ev, col, totalCols, startMin, endMin }) => {
                    const top = minToY(startMin);
                    const height = Math.max(20, minToY(endMin - startMin));
                    const width = `calc(${(1 / totalCols) * 100}% - 4px)`;
                    const left = `calc(${(col / totalCols) * 100}% + 2px)`;
                    const evId = String(ev.instance_id || ev.id);
                    const isConflict = conflictEventIds.has(evId);
                    const bgColor = isConflict ? '#b91c1c' : getColor(ev.color);
                    const borderLeftStyle = isConflict ? '4px solid #f87171' : '4px solid rgba(0,0,0,0.2)';
                    const boxShadow = isConflict ? '0 0 14px rgba(239,68,68,0.7)' : undefined;
                    return (
                      <div key={ev.instance_id || ev.id}
                        data-evid={ev.instance_id || ev.id}
                        className={cn("absolute pointer-events-auto cursor-pointer select-none overflow-hidden group", !canEditEvent(ev) && "cursor-default")}
                        style={{ top, height, width, left, borderRadius: 5, background: bgColor, color: '#fff', borderLeft: borderLeftStyle, boxShadow, zIndex: (isConflict ? 60 : 10) + col, transition: 'box-shadow .12s, filter .1s', boxSizing: 'border-box', minHeight: 20, opacity: ev.resolution === 'draft' ? 0.6 : 1.0 }}
                        onClick={(e) => handleEventClick(e, ev)}
                        onMouseDown={(e) => { e.stopPropagation(); if (canEditEvent(ev)) handleEventMouseDown(e, ev, di); }}
                        onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.06)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.28)'; e.currentTarget.style.zIndex = '200'; }}
                        onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.boxShadow = ''; e.currentTarget.style.zIndex = String(10 + col); }}
                        data-testid={`event-${ev.instance_id || ev.id}`}>
                        {/* Top resize handle (hidden if can't edit) */}
                        {canEditEvent(ev) && (
                          <div data-rz="top"
                            className="absolute top-0 left-0 right-0 h-[0.5625rem] cursor-ns-resize z-20 flex items-center justify-center rounded-t-[0.3125rem]"
                            onMouseDown={(e) => handleResizeMouseDown(e, ev, 'top')}>
                            <div className="w-8 h-[0.1875rem] rounded bg-white/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}
                        {/* Content */}
                        <div className="px-2 py-1 flex flex-col h-full min-w-0">
                          {(() => {
                            let fId = ev.faculty_id;
                            let bId = ev.batch_id;
                            if (ev.assignment_id) {
                              const assign = assignments.find(a => String(a.id) === String(ev.assignment_id));
                              if (assign) {
                                fId = fId || assign.faculty_id;
                                bId = bId || assign.batch_id;
                              }
                            }
                            const fName = faculty.find(f => String(f.id) === String(fId))?.name;
                            
                            // Resolve all batch names (primary bId and any batch_* from calendar_ids)
                            const batchIds = new Set();
                            if (bId) batchIds.add(String(bId));
                            if (ev.calendar_ids && Array.isArray(ev.calendar_ids)) {
                              ev.calendar_ids.forEach(cid => {
                                if (typeof cid === 'string' && cid.startsWith('batch_')) {
                                  batchIds.add(cid.substring(6));
                                }
                              });
                            }
                            const eventBatches = Array.from(batchIds)
                              .map(bid => batches.find(b => String(b.id) === String(bid))?.name)
                              .filter(Boolean);
                            
                            return (
                              <>
                                {/* Stacked Batch Names */}
                                {eventBatches.map((name, idx) => (
                                  <div key={idx} className="text-[0.625rem] font-bold uppercase tracking-wider text-white/90 truncate leading-none mb-0.5">
                                    {name}
                                  </div>
                                ))}

                                <div className={cn("text-[0.75rem] font-semibold leading-tight truncate mt-0.5", height > 35 ? "mb-0.5" : "")}>
                                  {ev.title || 'Untitled'}
                                </div>
                                
                                {height > 45 && fName && (
                                  <div className="text-[0.65625rem] opacity-90 truncate leading-none font-medium mt-0.5 text-white/80">
                                    👤 {fName}
                                  </div>
                                )}
                              </>
                            );
                          })()}

                          {height > 55 && (
                            <div data-timelbl="1" className="text-[0.625rem] opacity-80 mt-1 truncate">
                              {formatMinutes(startMin)} – {formatMinutes(endMin)}
                            </div>
                          )}
                          
                          {ev.match_level && height > 75 && (
                            <div className="inline-block self-start mt-1 px-1 py-[1px] rounded bg-black/20 text-[0.45rem] uppercase font-bold tracking-wider">
                              {ev.match_level.replace('_', ' ')}
                            </div>
                          )}
                          
                          {ev.is_recurring_instance && <span className="absolute bottom-1 right-1 text-[0.5rem] opacity-70">🔁</span>}
                        </div>
                        {/* Bottom resize handle (hidden if can't edit) */}
                        {canEditEvent(ev) && (
                          <div data-rz="bottom"
                            className="absolute bottom-0 left-0 right-0 h-[0.5625rem] cursor-ns-resize z-20 flex items-center justify-center rounded-b-[0.3125rem]"
                            onMouseDown={(e) => handleResizeMouseDown(e, ev, 'bottom')}>
                            <div className="w-8 h-[0.1875rem] rounded bg-white/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}
                        {/* RBAC restricted overlay */}
                        {!canEditEvent(ev) && (
                          <div className="absolute inset-0 rounded-[5px] pointer-events-none" style={{ background: 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.08) 3px, rgba(0,0,0,0.08) 6px)', zIndex: 25 }} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ── Template Cutout Overlay (React-driven, uses same layoutEvents) ── */}
                {dragHoverDayStr === dayStr && draggedTemplate && (() => {
                  const tEvents = typeof draggedTemplate.events === 'string' ? JSON.parse(draggedTemplate.events) : draggedTemplate.events;
                  if (!tEvents || !tEvents.length) return null;

                  // Adapt template events into the format layoutEvents expects
                  // Parse dayStr safely instead of string parsing to fix "shown in the starting of the day" (Invalid Date -> NaN CSS bug)
                  const [yyyy, mm, dd] = dayStr.split('-').map(Number);
                  const pseudoEvents = tEvents.map((te, i) => {
                    const startMin = gridStartMinutes + te.start_slot * 15;
                    const durMin = te.duration_slots * 15;
                    const baseDate = new Date(yyyy, mm - 1, dd, 0, 0, 0);
                    const sDate = new Date(baseDate); sDate.setMinutes(startMin);
                    const eDate = new Date(baseDate); eDate.setMinutes(startMin + durMin);
                    const assign = assignments?.find(a => a.id === te.assignment_id);
                    return {
                      id: `tpl_preview_${i}`,
                      instance_id: `tpl_preview_${i}`,
                      title: te.subject_name || 'Event',
                      start_time: sDate,
                      end_time: eDate,
                      color: assign?.color || 'blue',
                      faculty_id: assign?.faculty_id || te.faculty_id,
                      batch_id: assign?.batch_id || te.batch_id,
                      event_type: 'lecture',
                      _isTemplatePreview: true,
                    };
                  });

                  const previewLaid = layoutEvents(pseudoEvents);

                  return (
                    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 42 }} data-testid="template-cutout-overlay">
                      {previewLaid.map(({ ev: pev, col: pCol, totalCols: pTotal, startMin: pStart, endMin: pEnd }) => {
                        const pTop = minToY(pStart);
                        const pHeight = Math.max(20, minToY(pEnd - pStart));
                        const pWidth = `calc(${(1 / pTotal) * 100}% - 4px)`;
                        const pLeft = `calc(${(pCol / pTotal) * 100}% + 2px)`;
                        const bgColor = getColor(pev.color);
                        return (
                          <div key={pev.id}
                            className="absolute select-none overflow-hidden"
                            style={{
                              top: pTop, height: pHeight, width: pWidth, left: pLeft,
                              borderRadius: 5, background: bgColor, color: '#fff',
                              borderLeft: '4px solid rgba(0,0,0,0.2)',
                              zIndex: 42 + pCol, boxSizing: 'border-box', minHeight: 20,
                              opacity: 0.75, border: '2px dashed rgba(255,255,255,0.6)',
                            }}>
                            <div className="px-2 py-1 flex flex-col h-full min-w-0">
                              <div className="text-[0.75rem] font-semibold leading-tight truncate">{pev.title}</div>
                              
                              {pHeight > 45 && (pev.batch_id || pev.faculty_id) && (
                                <div className="text-[0.65625rem] opacity-90 truncate leading-none font-medium mt-0.5">
                                  {[
                                    faculty.find(f => String(f.id) === String(pev.faculty_id))?.name,
                                    batches.find(b => String(b.id) === String(pev.batch_id))?.name
                                  ].filter(Boolean).join(' • ')}
                                </div>
                              )}

                              {pHeight > 30 && <div className="text-[0.65625rem] opacity-90 mt-1">{formatMinutes(pStart)} – {formatMinutes(pEnd)}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Current time line */}
                {isToday(day) && currentTime && (() => {
                  const mins = getHours(currentTime) * 60 + getMinutes(currentTime);
                  return (
                    <div className="absolute left-0 right-0 pointer-events-none" style={{ top: minToY(mins), zIndex: 50 }}>
                      <div className="w-3 h-3 rounded-full bg-red-500 absolute -left-1.5 -top-1.5" />
                      <div className="h-[0.125rem] bg-red-500 w-full" />
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── Month Grid ─────────────────────────────────────────
export function MonthGrid({ events, currentDate, onDayClick, onEventClick, faculty, batches, conflictEventIds = new Set() }) {
  const assignments = useCalendarStore(s => s.assignments);
  const monthStart = startOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
  const allDays = useMemo(() => {
    const arr = [];
    let d = calStart;
    while (d <= calEnd) { arr.push(d); d = addDays(d, 1); }
    return arr;
  }, [calStart.getTime(), calEnd.getTime()]);

  // Pre-group events by day string (O(1) lookup per cell instead of O(N) filter)
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
      const key = format(s, 'yyyy-MM-dd');
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    }
    return map;
  }, [events]);

  const getEventsForDay = (dd) => eventsByDay[format(dd, 'yyyy-MM-dd')] || [];

  return (
    <div className="flex flex-col flex-1 p-3 overflow-hidden">
      <div className="grid grid-cols-7 border-b border-zinc-200 dark:border-zinc-700/50 flex-shrink-0">
        {DAYS_LABEL.map(l => (
          <div key={l} className="text-center py-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-zinc-400">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 flex-1 border border-zinc-200 dark:border-zinc-700/50 rounded-lg overflow-hidden" data-testid="month-grid">
        {allDays.map((dd, i) => {
          const dayEvents = getEventsForDay(dd);
          const inMonth = isSameMonth(dd, currentDate);
          return (
            <div key={i} className={cn("min-h-[5.0rem] p-1.5 border-b border-r border-zinc-100 dark:border-zinc-800/50 cursor-pointer transition-colors hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20", !inMonth && "bg-zinc-50/50 dark:bg-zinc-900/50", isToday(dd) && "bg-blue-50/30 dark:bg-blue-900/5")}
              onClick={() => onDayClick?.(dd)} data-testid={`month-day-${format(dd, 'yyyy-MM-dd')}`}>
              <div className={cn("text-xs mb-1 w-6 h-6 flex items-center justify-center rounded-full", isToday(dd) ? "bg-blue-600 text-white font-bold" : inMonth ? "text-zinc-700 dark:text-zinc-300" : "text-zinc-300 dark:text-zinc-600")}>
                {format(dd, 'd')}
              </div>
              {dayEvents.slice(0, 3).map((ev, ei) => (
                <div key={ev.instance_id || ev.id || ei}
                  className={cn(
                    "text-[0.65625rem] px-1.5 py-0.5 mb-0.5 rounded truncate cursor-pointer font-medium border",
                    ev.event_type === 'availability' ? "bg-white/90 !text-zinc-600 border-zinc-200 border-dashed" : "text-white border-transparent"
                  )}
                  style={(() => {
                    const evId = String(ev.instance_id || ev.id);
                    const isConflict = conflictEventIds.has(evId);
                    if (ev.event_type === 'availability') {
                      return { borderColor: isConflict ? '#ef4444' : getColor(ev.color), opacity: ev.resolution === 'draft' ? 0.6 : 1.0 };
                    }
                    return { background: isConflict ? '#b91c1c' : getColor(ev.color), border: isConflict ? '1px solid #f87171' : undefined, boxShadow: isConflict ? '0 0 8px rgba(239,68,68,0.6)' : undefined, opacity: ev.resolution === 'draft' ? 0.6 : 1.0 };
                  })()}
                  onClick={e => { e.stopPropagation(); onEventClick?.(ev); }}>
                  {getEventLabel(ev, faculty, batches)}
                </div>
              ))}
              {dayEvents.length > 3 && <div className="text-[0.625rem] text-zinc-400 pl-1 cursor-pointer hover:text-blue-500">+{dayEvents.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Mini Calendar ──────────────────────────────────────
export function MiniCalendar({ currentDate, selectedDate, onDateSelect }) {
  const [viewDate, setViewDate] = useState(currentDate);
  useEffect(() => { setViewDate(currentDate); }, [currentDate]);
  const monthStart = startOfMonth(viewDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 });
  const allDays = [];
  let dd = calStart;
  while (dd <= calEnd) { allDays.push(dd); dd = addDays(dd, 1); }

  return (
    <div className="select-none" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-[0.65rem] font-bold uppercase tracking-widest text-[#71717a] dark:text-zinc-400" style={{ color: 'var(--sidebar-muted)' }}>{format(viewDate, 'MMMM yyyy')}</span>
        <div className="flex gap-1">
          <button className="w-5 h-5 rounded hover:bg-[var(--sidebar-accent)] flex items-center justify-center text-[var(--sidebar-muted)] hover:text-[var(--sidebar-fg)] text-xs transition-colors" onClick={() => setViewDate(subMonths(viewDate, 1))}>&lt;</button>
          <button className="w-5 h-5 rounded hover:bg-[var(--sidebar-accent)] flex items-center justify-center text-[var(--sidebar-muted)] hover:text-[var(--sidebar-fg)] text-xs transition-colors" onClick={() => setViewDate(addMonths(viewDate, 1))}>&gt;</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px text-center text-[0.625rem]">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((l, i) => <div key={i} className="py-1 font-semibold opacity-60" style={{ color: 'var(--sidebar-muted)' }}>{l}</div>)}
        {allDays.map((day, i) => {
          const inM = isSameMonth(day, viewDate), today = isToday(day), sel = selectedDate && isSameDay(day, selectedDate);
          return (
            <div key={i} className={cn("py-1 rounded-md cursor-pointer transition-all duration-150 relative", 
              !inM && "opacity-25", 
              today && "bg-blue-600 text-white font-bold", 
              sel && !today && "bg-blue-500/20 text-blue-600 dark:text-blue-400 font-semibold", 
              !today && !sel && "hover:bg-[var(--sidebar-accent)]")}
              style={{ color: !today && !sel ? 'var(--sidebar-fg)' : undefined }}
              onClick={() => onDateSelect(day)}>
              {format(day, 'd')}
              {today && <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-0.5 h-0.5 bg-white rounded-full" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Agenda View ────────────────────────────────────────
export function AgendaView({ events, currentDate, onEventClick, faculty, batches, conflictEventIds = new Set() }) {
  const assignments = useCalendarStore(s => s.assignments);
  // Show events for the current month + 2 months (3 months total)
  const yr = currentDate.getFullYear();
  const mo = currentDate.getMonth();
  const rangeStart = new Date(yr, mo, 1);
  const rangeEnd = new Date(yr, mo + 3, 0);

  // Group events by date
  const groups = useMemo(() => {
    const map = {};
    let d = new Date(rangeStart);
    while (d <= rangeEnd) {
      const ds = format(d, 'yyyy-MM-dd');
      const dayEvs = events.filter(ev => {
        const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
        return isSameDay(s, d);
      }).sort((a, b) => {
        const as = typeof a.start_time === 'string' ? parseISO(a.start_time) : a.start_time;
        const bs = typeof b.start_time === 'string' ? parseISO(b.start_time) : b.start_time;
        return as - bs;
      });
      if (dayEvs.length > 0) map[ds] = dayEvs;
      d = addDays(d, 1);
    }
    return map;
  }, [events, rangeStart.getTime(), rangeEnd.getTime()]);

  const sortedDates = Object.keys(groups).sort();
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  if (sortedDates.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto p-6">
        <div className="text-center py-20 text-zinc-400">
          <div className="text-5xl mb-4">📅</div>
          <div className="text-lg font-medium mb-1">No upcoming events</div>
          <div className="text-sm">Click "New Event" to create one!</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="agenda-view">
      {sortedDates.map(ds => {
        const dd = parseISO(ds);
        const isTod = ds === todayStr;
        const dayEvents = groups[ds];
        return (
          <div key={ds} className="flex gap-4 mb-0" data-testid={`agenda-group-${ds}`}>
            {/* Date column */}
            <div className="w-[4.5rem] flex-shrink-0 text-right pt-2.5">
              <div className="text-[0.625rem] font-bold uppercase tracking-wider text-zinc-400">
                {format(dd, 'EEE')}
              </div>
              <div className={cn(
                "text-[1.75rem] font-light leading-tight",
                isTod ? "text-blue-500 font-semibold" : "text-zinc-400"
              )}>
                {format(dd, 'd')}
              </div>
            </div>
            {/* Events column */}
            <div className="flex-1 border-t border-zinc-200 dark:border-zinc-700/50 pt-2 pb-4">
              {dayEvents.map(ev => {
                const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
                const e = typeof ev.end_time === 'string' ? parseISO(ev.end_time) : ev.end_time;
                const bgColor = getColor(ev.color);
                const timeStr = ev.is_all_day
                  ? 'All day'
                  : `${format(s, 'h:mm a')} – ${format(e, 'h:mm a')}`;
                return (
                  <div
                    key={ev.instance_id || ev.id}
                    className={cn(
                      "flex items-center gap-3 px-2.5 py-2 rounded-lg cursor-pointer transition-colors",
                      conflictEventIds.has(String(ev.instance_id || ev.id))
                        ? "bg-red-50/80 dark:bg-red-900/20 ring-1 ring-red-400/50"
                        : "hover:bg-zinc-100 dark:hover:bg-zinc-800/50"
                    )}
                    style={{ opacity: ev.resolution === 'draft' ? 0.6 : 1.0 }}
                    onClick={() => onEventClick?.(ev)}
                    data-testid={`agenda-event-${ev.instance_id || ev.id}`}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: conflictEventIds.has(String(ev.instance_id || ev.id)) ? '#ef4444' : bgColor, boxShadow: conflictEventIds.has(String(ev.instance_id || ev.id)) ? '0 0 6px rgba(239,68,68,0.8)' : undefined }} />
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 min-w-[7.5rem]">
                      {timeStr}
                    </div>
                    <div className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                      {getEventLabel(ev, faculty, batches, assignments)}
                      {ev.is_recurring_instance && (
                        <span className="text-[0.6875rem] text-zinc-400 ml-1">🔁</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { HOUR_PX, COLOR_MAP, getColor, formatMinutes };
